import { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { toast } from 'sonner';
import { Loader2, Wand2, ChevronDown, ChevronUp } from 'lucide-react';
import { safeJson } from '../lib/safeJson';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const headers = () => {
  const t = localStorage.getItem('hrms-token');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
};

/** Correct ONLY the used days. Allotted stays locked; balance = allotted − used. */
export const UsedDaysDialog = ({ open, employee, leaveTypes = [], defaultType = '', onClose, onDone }) => {
  const [leaveType, setLeaveType] = useState(defaultType);
  const [info, setInfo] = useState(null);
  const [used, setUsed] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showRecords, setShowRecords] = useState(false);

  const load = useCallback(async (type) => {
    if (!employee || !type) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/leaves/balance/${employee.employee_id}/used-breakdown?leave_type=${encodeURIComponent(type)}&year=all`,
        { headers: headers(), credentials: 'include' });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Could not load the breakdown');
      setInfo(d);
      setUsed(String(d.used));
    } catch (e) { toast.error(e.message); setInfo(null); }
    finally { setLoading(false); }
  }, [employee]);

  useEffect(() => {
    if (open) {
      const t = defaultType || leaveTypes[0] || '';
      setLeaveType(t); setReason(''); setShowRecords(false);
      load(t);
    }
  }, [open, defaultType, leaveTypes, load]);

  const allotted = info?.allotted ?? 0;
  const newBalance = Math.max(allotted - (parseFloat(used) || 0), 0);

  const save = async () => {
    if (used === '' || isNaN(parseFloat(used))) { toast.error('Enter the used days'); return; }
    if (!reason.trim()) { toast.error('Write why the used days are being corrected'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/leaves/balance/${employee.employee_id}/used`, {
        method: 'PUT', headers: headers(), credentials: 'include',
        body: JSON.stringify({ leave_type: leaveType, used: parseFloat(used), reason: reason.trim() }),
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Could not update the used days');
      toast.success(d.message);
      if (d.warning) toast.warning(d.warning);
      onDone?.();
      onClose?.();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="used-days-dialog">
        <DialogHeader>
          <DialogTitle>Correct Used Days — {employee?.employee_name}</DialogTitle>
          <DialogDescription>
            The allotted days stay exactly as they are. Only the used days change, and the balance is recalculated.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Leave Policy</Label>
            <Select value={leaveType} onValueChange={(v) => { setLeaveType(v); load(v); }}>
              <SelectTrigger data-testid="used-leave-type"><SelectValue placeholder="Select leave type" /></SelectTrigger>
              <SelectContent>
                {leaveTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading records...
            </div>
          ) : info && (
            <>
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-md border p-2 bg-slate-50 dark:bg-slate-800">
                  <p className="text-[10px] uppercase text-slate-500">Allotted (locked)</p>
                  <p className="text-lg font-semibold" data-testid="used-allotted">{allotted}</p>
                </div>
                <div className="rounded-md border p-2">
                  <p className="text-[10px] uppercase text-slate-500">Used</p>
                  <Input type="number" step="0.5" min="0" max={allotted} value={used}
                    onChange={e => setUsed(e.target.value)} className="h-8 text-lg font-semibold"
                    data-testid="used-input" />
                </div>
                <div className="rounded-md border p-2 bg-emerald-50 dark:bg-emerald-950">
                  <p className="text-[10px] uppercase text-slate-500">Balance</p>
                  <p className="text-lg font-semibold text-emerald-700" data-testid="used-new-balance">{newBalance}</p>
                </div>
              </div>

              <div className="rounded-md border border-amber-300 bg-amber-50 p-2 space-y-1.5" data-testid="used-computed-box">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs text-amber-800">
                    System counted <strong>{info.computed_used}</strong> day(s) from {info.records.length} approved record(s)
                    {info.difference !== 0 ? ` — stored value differs by ${info.difference > 0 ? '+' : ''}${info.difference}` : ' — matches the stored value'}
                  </p>
                  <Button size="sm" variant="outline" className="h-7 text-xs shrink-0"
                    onClick={() => setUsed(String(info.computed_used))} data-testid="use-computed-btn">
                    <Wand2 className="w-3 h-3 mr-1" /> Use this
                  </Button>
                </div>
                <Button size="sm" variant="ghost" className="h-6 text-[11px] px-1"
                  onClick={() => setShowRecords(s => !s)} data-testid="toggle-records-btn">
                  {showRecords ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
                  {showRecords ? 'Hide' : 'Show'} the records behind this
                </Button>
                {showRecords && (
                  <div className="max-h-44 overflow-y-auto rounded border bg-white">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="h-7 text-[10px]">From</TableHead>
                          <TableHead className="h-7 text-[10px]">To</TableHead>
                          <TableHead className="h-7 text-[10px]">Days</TableHead>
                          <TableHead className="h-7 text-[10px]">Counted</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {info.records.length === 0 ? (
                          <TableRow><TableCell colSpan={4} className="text-xs text-slate-500 text-center">No approved records</TableCell></TableRow>
                        ) : info.records.map(r => (
                          <TableRow key={r.leave_id} data-testid={`used-record-${r.leave_id}`}>
                            <TableCell className="py-1 text-[11px]">{r.start_date}</TableCell>
                            <TableCell className="py-1 text-[11px]">{r.end_date}</TableCell>
                            <TableCell className="py-1 text-[11px]">{r.days}{r.half_day ? ' (half)' : ''}</TableCell>
                            <TableCell className="py-1 text-[11px] font-medium">{r.paid_days}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-xs">Reason for the correction <span className="text-red-600">*</span></Label>
                <Input value={reason} onChange={e => setReason(e.target.value)}
                  placeholder="e.g. half-day was wrongly counted as a full day"
                  data-testid="used-reason-input" />
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={saving || loading || !info} data-testid="used-save-btn">
              {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Save Used Days
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UsedDaysDialog;
